/**
 * Receiver-side playout-delay bounds (milliseconds) sent to Millicast on
 * connect. This is the biggest safe lever on stream latency — and, pulled the
 * other way, on stutter tolerance.
 *
 * The buffer is ADAPTIVE within [min, max]: a clean connection sits at
 * ~0.1-0.35s, but a degraded one walks UP toward max to absorb jitter. So
 * `max` is the ceiling on how much late-packet slack the player may take, and
 * `min` is what it starts with before the estimator has adapted — which is
 * what governs the first seconds after connect.
 *
 * THESE ARE PER-GAME. Low latency and stutter-resistance are the same dial
 * pulled in opposite directions, and the two game families want opposite ends
 * of it. Select with `playoutDelayForGame()` rather than importing a constant
 * directly.
 */

/**
 * CRASH ONLY. Crash trades buffer for immediacy: the player cashes out against
 * what they see, so glass-to-glass lag is what matters.
 *
 * The buffer used to ratchet and not release — measured in prod as a one-step
 * climb (a session stepped 1.42s -> 2.34s in a minute and locked there for
 * 45 min). A max of 1000ms let that sit on top of the ~1.2s median stream
 * latency, pinning degraded/mobile viewers at 2.3-2.5s of glass lag.
 *
 * min:0 removes a 100ms floor every viewer paid on clean networks for no
 * smoothness benefit (the estimator still grows above 0 when needed).
 * max:250 caps the degraded tail. Staged down from 1000; do NOT set {0,0}
 * blind — watch framesDropped/freezeCount in the RTC stats before going lower.
 *
 * Crash tolerates a thin buffer because its video is a synthesized frozen
 * frame through betting plus a pre-rendered clip — low motion, low bitrate,
 * and so comparatively forgiving. Live camera footage is NOT.
 *
 * This does NOT touch any exploit defence: it is a client playback buffer,
 * downstream of every server-side cash-out gate. It shrinks how far the video
 * trails the backend, which only ever shrinks the see-and-cash margin further
 * in the house's favour.
 */
export const CRASH_STREAM_PLAYOUT_DELAY_MS = { min: 0, max: 250 } as const

/**
 * EVERY OTHER GAME (CCTV counting, marble races). These are continuous live
 * camera footage at real motion, and nothing in them is timed against the
 * video — the outcome is a measured count or a seeded order, revealed at
 * settlement. They gain nothing from shaving glass-to-glass lag and lose a
 * lot to a thin buffer.
 *
 * {100, 1000} is the long-standing pre-2026-07-17 setting, restored here. It
 * was briefly replaced fleet-wide by the crash profile above — that constant
 * was applied in useStream with no game gate — leaving mobile/5G viewers of
 * the CCTV games ~0ms of buffer at connect (the worst moment) and a 250ms
 * ceiling that could not grow to absorb jitter. Result: heavy stutter on
 * first load over a poor connection. The `max: 1000` exists precisely to give
 * a degraded connection somewhere to go; the line it replaced said so.
 */
export const DEFAULT_STREAM_PLAYOUT_DELAY_MS = { min: 100, max: 1000 } as const

export type PlayoutDelayBounds = Readonly<{ min: number; max: number }>

/**
 * Playout-delay profile for a stream. Crash opts IN to the low-latency
 * profile; everything else gets the buffered default — so a game added later
 * is stutter-safe by omission rather than by remembering to opt out.
 */
export const playoutDelayForGame = (isCrash: boolean): PlayoutDelayBounds =>
  isCrash ? CRASH_STREAM_PLAYOUT_DELAY_MS : DEFAULT_STREAM_PLAYOUT_DELAY_MS
